import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Activity, AlertTriangle, BadgeCheck, ExternalLink, HelpCircle, Info,
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import * as takata from '../services/takataService';
import {
  StatoPortale, SegnalazioneTakata,
  STATO_SEGNALAZIONE_LABEL, TIPO_SEGNALAZIONE_LABEL, etichettaMarcaTakata,
} from '../services/takataService';
import SegnalaProblema from '../components/takata/SegnalaProblema';
import LogoMarca from '../components/takata/LogoMarca';

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- helper di normalizzazione VIN conservato per le verifiche manuali dei portali
function normalizeVin(raw: string) {
  return (raw || '').toUpperCase().replace(/[\s\-_.]/g, '');
}

function fmtData(d?: string | null) {
  if (!d) return null;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()}`;
}

// Come viene verificata la marca: parola d'ordine breve + spiegazione.
function modoBadge(p: StatoPortale) {
  if (p.mode === 'auto') {
    return { label: 'Automatica', hint: 'La verifica viene fatta dal nostro server: non serve alcun gesto.', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' };
  }
  if (p.mode === 'guided') {
    return { label: 'Con l\'estensione', hint: 'Si apre il portale del costruttore e l\'estensione lavora sulla pagina.', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' };
  }
  if (p.mode === 'esclusa') {
    return { label: 'Non soggetta', hint: 'Marca non interessata dal richiamo: nessun portale da consultare.', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' };
  }
  return { label: 'Non disponibile', hint: 'Nessuna fonte utilizzabile al momento per questa marca.', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' };
}

const LIVELLO_LABEL: Record<string, string> = {
  automatico: 'Compila il telaio e avvia la verifica da sola',
  manuale: 'Compila il telaio, la verifica la avvii tu',
  semiautomatico: 'Compila il telaio, alcuni passaggi restano a te',
};

// Oltre questi giorni un controllo non e' piu' una notizia fresca: si mostra
// in grigio, cosi' un "funzionava" di mesi fa non sembra di ieri.
const GIORNI_CONTROLLO_FRESCO = 60;

function giorniFa(d?: string | null) {
  if (!d) return null;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return Math.floor((Date.now() - dt.getTime()) / 86400000);
}

const Fatto: React.FC<{ etichetta: string; children: React.ReactNode }> = ({ etichetta, children }) => (
  <div className="min-w-0">
    <p className="text-2xs uppercase tracking-wide text-gray-400 dark:text-gray-500">{etichetta}</p>
    <div className="text-sm text-gray-800 dark:text-gray-200">{children}</div>
  </div>
);

const Assente = () => <span className="text-gray-400 dark:text-gray-500">—</span>;

const TakataStatoPortaliPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [portali, setPortali] = useState<StatoPortale[]>([]);
  const [avvisi, setAvvisi] = useState<string[]>([]);
  const [nota, setNota] = useState('');
  const [mie, setMie] = useState<SegnalazioneTakata[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [stato, segn] = await Promise.all([
        takata.getStatoPortali(),
        takata.getMieSegnalazioni(),
      ]);
      setPortali(Array.isArray(stato.portali) ? stato.portali : []);
      setAvvisi(Array.isArray(stato.avvisi) ? stato.avvisi : []);
      setNota(stato.nota || '');
      setMie(Array.isArray(segn.items) ? segn.items : []);
    } catch (e) {
      toast.error('Errore nel caricamento dello stato dei portali');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const totaleAperte = useMemo(
    () => portali.reduce((n, p) => n + (p.segnalazioniAperte || 0), 0),
    [portali],
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6 px-4">
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate('/area-riservata/takata')} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Torna al registro
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center flex-shrink-0">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">Stato dei portali</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">Cosa aspettarsi da ogni marchio, prima di iniziare</p>
          </div>
        </div>

        {/* Nota onesta: le verifiche fallite non lasciano traccia */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4 text-sm text-blue-800 dark:text-blue-200 flex gap-2">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>
            {nota || 'I conteggi dicono quanto un portale viene usato e se funziona ancora, non quante volte ha fallito: le verifiche non riuscite non vengono registrate.'}
            {' '}Se un portale ti ha dato problemi, <strong>segnalalo</strong>: {'è l\'unico modo che abbiamo per saperlo.'}
          </p>
        </div>

        {totaleAperte > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4 text-sm text-amber-800 dark:text-amber-200 flex gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>Ci sono <strong>{totaleAperte}</strong> segnalazioni aperte non ancora risolte. I marchi interessati sono evidenziati qui sotto.</p>
          </div>
        )}

        {avvisi.length > 0 && (
          <div className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-4 text-xs text-gray-600 dark:text-gray-400">
            {avvisi.map((a, i) => <p key={i}>{a}</p>)}
          </div>
        )}

        {/* Stesso modulo del registro: un solo posto da correggere, e l'associato trova
            la stessa cosa nello stesso modo da entrambe le pagine. */}
        <div className="mb-6">
          <SegnalaProblema onInviata={load} />
        </div>

        {/* Elenco portali */}
        <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Marchi ({portali.length})</h2>
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Caricamento…</p>
        ) : portali.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Nessun portale da mostrare.</p>
        ) : (
          <div className="space-y-2 mb-6">
            {portali.map((p) => {
              const badge = modoBadge(p);
              const test = p.ultimoTest;
              const dataTest = fmtData(test?.data);
              const giorni = giorniFa(test?.data);
              const vecchio = giorni !== null && giorni > GIORNI_CONTROLLO_FRESCO;
              const allarme = p.segnalazioniAperte > 0;
              return (
                <div key={p.chiave}
                  className={`rounded-xl border p-3 bg-white dark:bg-gray-800 ${allarme ? 'border-amber-300 dark:border-amber-700' : 'border-gray-200 dark:border-gray-700'}`}>
                  {/* Il logo davanti al nome: in un elenco di trenta marchi tutti resi allo
                      stesso modo, è quello che permette di trovare il proprio a occhio. */}
                  <div className="flex items-start gap-3">
                  <LogoMarca nome={p.marca} size={36} />
                  <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{p.marca}</span>
                    <span className={`text-2xs px-1.5 py-0.5 rounded-full font-medium ${badge.cls}`} title={badge.hint}>{badge.label}</span>
                    {/* Il badge esiste in ENTRAMBE le direzioni: senza il gemello neutro,
                        "mai provato" si sarebbe dedotto dall'assenza del verde, cioe' non
                        si sarebbe distinto da un dato mancante. Il grigio non e' un
                        allarme: e' un'informazione. */}
                    {p.ricetta?.propria && (p.validato ? (
                      <span className="text-2xs px-1.5 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 flex items-center gap-1">
                        <BadgeCheck className="w-3 h-3" /> {'provato su un\'auto vera'}
                      </span>
                    ) : (
                      <span className="text-2xs px-1.5 py-0.5 rounded-full font-medium bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 flex items-center gap-1"
                        title={'Le istruzioni per questo portale esistono, ma nessuno le ha ancora provate su un veicolo reale.'}>
                        <HelpCircle className="w-3 h-3" /> {'mai provato su un\'auto vera'}
                      </span>
                    ))}
                    {p.ricettaGenerica && (
                      <span className="text-2xs px-1.5 py-0.5 rounded-full font-medium bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                        title={'Non abbiamo ancora accertato cosa riesca a fare l\'estensione su questo portale.'}>
                        istruzioni generiche
                      </span>
                    )}
                    {allarme && (
                      <span className="text-2xs px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> {p.segnalazioniAperte} segnalazioni aperte
                      </span>
                    )}
                    {p.portaleUrl && (
                      <a href={p.portaleUrl} target="_blank" rel="noopener noreferrer"
                        className="ml-auto text-2xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1">
                        portale <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                    <Fatto etichetta="Automazione">
                      {p.livello
                        ? <span title={LIVELLO_LABEL[p.livello] || ''}>{LIVELLO_LABEL[p.livello] || p.livello}</span>
                        : p.ricettaGenerica
                          ? <span className="text-gray-500 dark:text-gray-400">non ancora accertata</span>
                          : <Assente />}
                    </Fatto>
                    <Fatto etichetta="Ultimo controllo">
                      {test && dataTest ? (
                        <span
                          className={
                            test.esito === 'non_funziona'
                              ? 'text-red-600 dark:text-red-400'
                              : vecchio
                                ? 'text-gray-500 dark:text-gray-400'
                                : 'text-green-700 dark:text-green-400'
                          }
                          title={vecchio ? `Controllo di ${giorni} giorni fa: dice com'era allora, non com'e' oggi.` : undefined}
                        >
                          {test.esito === 'non_funziona' ? 'non funzionava' : 'funzionava'} il {dataTest}
                        </span>
                      ) : <Assente />}
                    </Fatto>
                    <Fatto etichetta="Verifiche 30 giorni">
                      {p.verifiche30gg > 0 ? p.verifiche30gg : <Assente />}
                    </Fatto>
                    <Fatto etichetta="Ultima verifica">
                      {fmtData(p.ultimaVerifica) || <Assente />}
                    </Fatto>
                  </div>

                  {test?.nota && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Nota del controllo: {test.nota}</p>
                  )}
                  {/* Il captcha si nomina solo quando lo sappiamo davvero: sulle voci
                      generiche il backend lo lascia a null e qui si dice invece che
                      non l'abbiamo ancora accertato. */}
                  {p.captcha && p.captcha !== 'nessuno' && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Il portale usa un captcha ({p.captcha}): va risolto a mano.</p>
                  )}
                  {p.ricettaGenerica && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Istruzioni generiche: non abbiamo ancora accertato cosa riesca a fare l'estensione su questo portale.
                      Potrebbe non compilare il telaio e potrebbe esserci un captcha. Se lo provi, {'segnalaci com\'è andata'}.
                    </p>
                  )}
                  </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Le mie segnalazioni */}
        <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Le mie segnalazioni ({mie.length})</h2>
        {mie.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Non hai ancora inviato segnalazioni.</p>
        ) : (
          <div className="space-y-1.5 mb-6">
            {mie.map((s) => (
              <div key={s._id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-2.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{etichettaMarcaTakata(s.marca)}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{TIPO_SEGNALAZIONE_LABEL[s.tipo] || s.tipo}</span>
                  {s.vin && <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{s.vin}</span>}
                  <span className={`ml-auto text-2xs px-1.5 py-0.5 rounded-full font-medium ${
                    s.stato === 'risolta'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                      : s.stato === 'presa_in_carico'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                  }`}>
                    {STATO_SEGNALAZIONE_LABEL[s.stato] || s.stato}
                  </span>
                </div>
                {s.descrizione && <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">{s.descrizione}</p>}
                <p className="text-2xs text-gray-400 dark:text-gray-500 mt-1">{fmtData(s.createdAt)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TakataStatoPortaliPage;
